import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@myvault/shared/hooks';
import { Spinner } from '@myvault/shared/components';

import { AppLayout } from '../layouts/AppLayout';
import { AuthLayout } from '../layouts/AuthLayout';
import { SearchProvider } from '../features/search';

// Eager-loaded routes (core functionality - first page load)
import { Register, ForgotPassword, AuthCallback } from '../features/auth';
import { LoginPage as Login } from '../features/auth/pages/LoginPage';
import { MagicLinkPage } from '../features/auth/pages/MagicLinkPage';
import { Dashboard } from '../features/dashboard/Dashboard';
import { NotFoundPage } from '../features/errors';
import { DocumentList } from '../features/documents';

// Lazy-loaded: Onboarding (one-time flow) - SCR-810
const OnboardingWizard = lazy(() =>
  import('../features/onboarding').then((mod) => ({ default: mod.OnboardingPage }))
);

// Lazy-loaded: Document detail views (navigated to from list)
const DocumentDetail = lazy(() =>
  import('../features/documents/DocumentDetail').then((mod) => ({ default: mod.DocumentDetail }))
);
const DocumentUpload = lazy(() =>
  import('../features/documents/DocumentUpload').then((mod) => ({ default: mod.DocumentUpload }))
);

// Lazy-loaded: Notifications (accessed via bell icon)
const NotificationsPage = lazy(() =>
  import('../features/notifications').then((mod) => ({ default: mod.NotificationsPage }))
);

// Lazy-loaded: Settings routes (BRD-008)
const SettingsLayout = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.SettingsLayout }))
);
const ProfileSettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.ProfileSettings }))
);
const SecuritySettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.SecuritySettings }))
);
const SessionsSettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.SessionsSettings }))
);
const NotificationSettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.NotificationSettings }))
);
const DataExportSettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.DataExportSettings }))
);
const AccountDeletionSettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.AccountDeletionSettings }))
);
// BRD-013: Client-Side Encryption Settings
const EncryptionSettings = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.EncryptionSettings }))
);
const EncryptionSetupWizard = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.EncryptionSetupWizard }))
);
const EncryptionRecoveryWizard = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.EncryptionRecoveryWizard }))
);
const ShareAccessHistoryPage = lazy(() =>
  import('../features/settings').then((mod) => ({ default: mod.ShareAccessHistoryPage }))
);

// Lazy-loaded: Additional BRD-008 routes
const ChangePasswordPage = lazy(() =>
  import('../features/auth').then((mod) => ({ default: mod.ChangePasswordPage }))
);
const TwoFactorSetupPage = lazy(() =>
  import('../features/two-factor-auth').then((mod) => ({ default: mod.TwoFactorSetupPage }))
);
const TermsAcceptancePage = lazy(() =>
  import('../features/auth').then((mod) => ({ default: mod.TermsAcceptancePage }))
);
const RegistrationPage = lazy(() =>
  import('../features/auth').then((mod) => ({ default: mod.RegistrationPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('../features/auth').then((mod) => ({ default: mod.ForgotPasswordPage }))
);
const HelpCenterPage = lazy(() =>
  import('../features/help').then((mod) => ({ default: mod.HelpCenterPage }))
);

// Lazy-loaded: Security Info page (JM-001 Trust Foundation)
const SecurityInfoPage = lazy(() =>
  import('../features/security').then((mod) => ({ default: mod.SecurityInfoPage }))
);

// Lazy-loaded: Prompt Builder routes (BRD-016)
const TemplateLibraryPage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.TemplateLibraryPage }))
);
const PromptBuilderPage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.PromptBuilderPage }))
);
const PromptPreviewPage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.PromptPreviewPage }))
);
const QuestionnairePage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.QuestionnairePage }))
);
const MetadataProfilePage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.MetadataProfilePage }))
);
// SCR-320: Client Analysis Prompt Generator (Professional)
const ClientAnalysisPromptPage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.ClientAnalysisPromptPage }))
);
// SCR-321: Expert Content Contribution (Professional)
const ExpertContentPage = lazy(() =>
  import('../features/prompt-builder').then((mod) => ({ default: mod.ExpertContentPage }))
);

// Lazy-loaded: Questionnaire routes (BRD-018)
const QuestionnaireWizard = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.QuestionnaireWizard }))
);
// SCR-QC-001: Questionnaire List (Admin)
const QuestionnaireListPage = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.QuestionnaireListPage }))
);
// SCR-QC-002: Visual Flow Builder (Admin)
const VisualFlowBuilderPage = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.VisualFlowBuilderPage }))
);
// SCR-QC-005: Analytics Dashboard (Admin)
const AnalyticsDashboardPage = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.AnalyticsDashboardPage }))
);
// SCR-QC-008: Preview Mode (Admin)
const PreviewModePage = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.PreviewModePage }))
);
// SCR-QC-006: Questionnaire Template Library (Admin)
const QuestionnaireTemplateLibraryPage = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.TemplateLibraryPage }))
);
// SCR-QC-007: Version History (Admin)
const VersionHistoryPage = lazy(() =>
  import('../features/questionnaires').then((mod) => ({ default: mod.VersionHistoryPage }))
);

// Lazy-loaded: Context Profile routes (BRD-020)
const ProfileListPage = lazy(() =>
  import('../features/context-profiles').then((mod) => ({ default: mod.ProfileListPage }))
);
const ProfileEditorPage = lazy(() =>
  import('../features/context-profiles').then((mod) => ({ default: mod.ProfileEditorPage }))
);

// Lazy-loaded: Subscription routes (BRD-009)
const CurrentPlan = lazy(() =>
  import('../features/subscription').then((mod) => ({ default: mod.CurrentPlan }))
);
const PlanChange = lazy(() =>
  import('../features/subscription').then((mod) => ({ default: mod.PlanChange }))
);
const PaymentMethods = lazy(() =>
  import('../features/subscription').then((mod) => ({ default: mod.PaymentMethods }))
);
const BillingHistory = lazy(() =>
  import('../features/subscription').then((mod) => ({ default: mod.BillingHistory }))
);
const CancelSubscription = lazy(() =>
  import('../features/subscription').then((mod) => ({ default: mod.CancelSubscription }))
);

// Lazy-loaded: Redaction Settings routes (BRD-014)
const RedactionTemplateList = lazy(() =>
  import('../features/redaction-settings').then((mod) => ({ default: mod.TemplateListPage }))
);
const RedactionTemplateEditor = lazy(() =>
  import('../features/redaction-settings').then((mod) => ({ default: mod.TemplateEditorPage }))
);
const RedactionTemplateAnalytics = lazy(() =>
  import('../features/redaction-settings').then((mod) => ({ default: mod.TemplateAnalyticsPage }))
);
// GAP-064/065/066: Template Coverage Overview
const RedactionTemplateCoverage = lazy(() =>
  import('../features/redaction-settings').then((mod) => ({ default: mod.TemplateCoveragePage }))
);

// Lazy-loaded: Redaction Interface routes (BRD-015)
const RedactionInterface = lazy(() =>
  import('../features/redaction').then((mod) => ({ default: mod.RedactionInterface }))
);

// Lazy-loaded: AI routes (BRD-002)
const AISummaryView = lazy(() =>
  import('../features/ai').then((mod) => ({ default: mod.AISummaryView }))
);
const DocumentComparison = lazy(() =>
  import('../features/ai').then((mod) => ({ default: mod.DocumentComparison }))
);
const AIRouter = lazy(() =>
  import('../features/ai').then((mod) => ({ default: mod.AIRouter }))
);

// Lazy-loaded: Delegation routes (BRD-003)
const DelegatesDashboard = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.DelegatesDashboard }))
);
const DelegatesManagement = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.DelegatesManagement }))
);
const EmergencyAccessSetup = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.EmergencyAccessSetup }))
);
const ActivityLog = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.ActivityLog }))
);
const PermissionTemplates = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.PermissionTemplates }))
);
// SCR-303: Delegate Detail & Edit
const DelegateDetail = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.DelegateDetailPage }))
);
// SCR-407: Bulk Delegate Operations
const BulkDelegateOperations = lazy(() =>
  import('../features/delegation').then((mod) => ({ default: mod.BulkDelegateOperations }))
);

// Lazy-loaded: Gemachtigde routes (BRD-004)
const SharedVaultsList = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.SharedVaultsList }))
);
const VaultDashboard = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.VaultDashboard }))
);
const DelegatedDocumentView = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.DelegatedDocumentView }))
);
const DelegateActivityHistory = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.DelegateActivityHistory }))
);
const AISummariesAccess = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.AISummariesAccess }))
);
const ExpirationMonitor = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.ExpirationMonitor }))
);
const OwnerCommunication = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.OwnerCommunication }))
);
const TransparencyView = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.TransparencyView }))
);
// GAP-028: Accept Invitation Page (public route for email links)
const AcceptInvitationPage = lazy(() =>
  import('../features/gemachtigde').then((mod) => ({ default: mod.AcceptInvitationPage }))
);

// Lazy-loaded: Nalatenschap (Estate) routes
const NalatenschapDossierPage = lazy(() =>
  import('../features/nalatenschap').then((mod) => ({ default: mod.NalatenschapDossierPage }))
);

// Lazy-loaded: Marketplace routes (BRD-011)
const MyServiceRequests = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.MyServiceRequests }))
);
// BenchmarkView wrapper to extract documentId from URL params
const BenchmarkViewWrapper = lazy(async () => {
  const { BenchmarkView } = await import('../features/marketplace');
  const { useParams } = await import('react-router-dom');
  const Component = () => {
    const { documentId } = useParams<{ documentId: string }>();
    if (!documentId) return <div>Document not found</div>;
    return <BenchmarkView documentId={documentId} />;
  };
  return { default: Component };
});
const ConnectionChat = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.ConnectionChat }))
);
const ProviderDashboard = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.ProviderDashboard }))
);
const ProviderRequestsFeed = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.ProviderRequestsFeed }))
);
const ProviderConnections = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.ProviderConnections }))
);
const ProviderRequestRespond = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.ProviderRequestRespond }))
);
const ProviderProfile = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.ProviderProfile }))
);
const MarketplaceBrowse = lazy(() =>
  import('../features/marketplace').then((mod) => ({ default: mod.MarketplaceBrowse }))
);

// Lazy-loaded: Persona-specific routes
const AdvisorDashboard = lazy(() => import('../features/advisor/Dashboard'));
const TenantAdmin = lazy(() => import('../features/tenant-admin/TenantAdmin'));

// Development-only routes (BRD-DEV-001)
// These are conditionally defined to ensure tree-shaking in production builds
const DevPersonaSwitcher = import.meta.env.DEV
  ? lazy(() => import('../features/dev').then((mod) => ({ default: mod.DevPersonaSwitcher })))
  : () => null;
const AnonymizeTestPage = import.meta.env.DEV
  ? lazy(() => import('../features/dev').then((mod) => ({ default: mod.AnonymizeTestPage })))
  : () => null;
const DevModePage = import.meta.env.DEV
  ? lazy(() => import('../features/dev').then((mod) => ({ default: mod.DevModePage })))
  : () => null;
const MockConfigPage = import.meta.env.DEV
  ? lazy(() => import('../features/dev').then((mod) => ({ default: mod.MockConfigPage })))
  : () => null;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function LoadingFallback() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

export function AppRoutes() {
  return (
    <SearchProvider>
      <Routes>
        {/* Public routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/register" element={<Register />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* New BRD-008 public routes (outside layout for full-page experience) */}
        <Route path="/register" element={<Suspense fallback={<LoadingFallback />}><RegistrationPage /></Suspense>} />
        <Route path="/forgot-password" element={<Suspense fallback={<LoadingFallback />}><ForgotPasswordPage /></Suspense>} />
        <Route path="/terms" element={<Suspense fallback={<LoadingFallback />}><TermsAcceptancePage /></Suspense>} />

        {/* GAP-028: Public invitation acceptance page (accessible from email links) */}
        <Route path="/invitation/:token" element={<Suspense fallback={<LoadingFallback />}><AcceptInvitationPage /></Suspense>} />

        {/* OAuth callback (public, no layout) */}
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Magic link verification (public, no layout) */}
        <Route path="/auth/magic-link" element={<MagicLinkPage />} />

        {/* Onboarding (protected but outside main layout) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingFallback />}>
                <OnboardingWizard />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<Dashboard />} />

          {/* Document routes */}
          <Route path="/documents" element={<DocumentList />} />
          <Route path="/documents/upload" element={<Suspense fallback={<LoadingFallback />}><DocumentUpload /></Suspense>} />
          <Route path="/documents/:id" element={<Suspense fallback={<LoadingFallback />}><DocumentDetail /></Suspense>} />

          {/* AI routes (BRD-002) */}
          <Route path="/documents/:documentId/summary" element={<Suspense fallback={<LoadingFallback />}><AISummaryView /></Suspense>} />
          <Route path="/documents/compare" element={<Suspense fallback={<LoadingFallback />}><DocumentComparison /></Suspense>} />
          <Route path="/ai/*" element={<Suspense fallback={<LoadingFallback />}><AIRouter /></Suspense>} />

          {/* Delegation routes (BRD-003) */}
          <Route path="/delegates" element={<Suspense fallback={<LoadingFallback />}><DelegatesDashboard /></Suspense>} />
          <Route path="/delegates/manage" element={<Suspense fallback={<LoadingFallback />}><DelegatesManagement /></Suspense>} />
          <Route path="/delegates/emergency-access" element={<Suspense fallback={<LoadingFallback />}><EmergencyAccessSetup /></Suspense>} />
          <Route path="/delegates/activity" element={<Suspense fallback={<LoadingFallback />}><ActivityLog /></Suspense>} />
          <Route path="/delegates/templates" element={<Suspense fallback={<LoadingFallback />}><PermissionTemplates /></Suspense>} />
          {/* SCR-407: Bulk Delegate Operations */}
          <Route path="/delegates/bulk" element={<Suspense fallback={<LoadingFallback />}><BulkDelegateOperations /></Suspense>} />
          {/* SCR-303: Delegate Detail & Edit */}
          <Route path="/delegates/:grantId" element={<Suspense fallback={<LoadingFallback />}><DelegateDetail /></Suspense>} />
          <Route path="/delegates/:grantId/edit" element={<Suspense fallback={<LoadingFallback />}><DelegateDetail /></Suspense>} />
          <Route path="/delegates/:grantId/extend" element={<Suspense fallback={<LoadingFallback />}><DelegateDetail /></Suspense>} />

          {/* Gemachtigde routes (BRD-004) */}
          <Route path="/gemachtigde/vaults" element={<Suspense fallback={<LoadingFallback />}><SharedVaultsList /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId" element={<Suspense fallback={<LoadingFallback />}><VaultDashboard /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/documents" element={<Suspense fallback={<LoadingFallback />}><DelegatedDocumentView /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/documents/:documentId" element={<Suspense fallback={<LoadingFallback />}><DelegatedDocumentView /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/documents/:documentId/ai" element={<Suspense fallback={<LoadingFallback />}><AISummariesAccess /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/ai" element={<Suspense fallback={<LoadingFallback />}><AISummariesAccess /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/expiring" element={<Suspense fallback={<LoadingFallback />}><ExpirationMonitor /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/messages" element={<Suspense fallback={<LoadingFallback />}><OwnerCommunication /></Suspense>} />
          <Route path="/gemachtigde/vaults/:vaultId/transparency" element={<Suspense fallback={<LoadingFallback />}><TransparencyView /></Suspense>} />
          <Route path="/gemachtigde/activity" element={<Suspense fallback={<LoadingFallback />}><DelegateActivityHistory /></Suspense>} />
          <Route path="/gemachtigde/expiring" element={<Suspense fallback={<LoadingFallback />}><ExpirationMonitor /></Suspense>} />

          {/* Notifications */}
          <Route path="/notifications" element={<Suspense fallback={<LoadingFallback />}><NotificationsPage /></Suspense>} />

          {/* Settings routes (BRD-008) */}
          <Route path="/settings" element={<Suspense fallback={<LoadingFallback />}><SettingsLayout /></Suspense>}>
            <Route index element={<Navigate to="/settings/profile" replace />} />
            <Route path="profile" element={<Suspense fallback={<LoadingFallback />}><ProfileSettings /></Suspense>} />
            <Route path="security" element={<Suspense fallback={<LoadingFallback />}><SecuritySettings /></Suspense>} />
            <Route path="security/change-password" element={<Suspense fallback={<LoadingFallback />}><ChangePasswordPage /></Suspense>} />
            <Route path="security/2fa" element={<Suspense fallback={<LoadingFallback />}><TwoFactorSetupPage /></Suspense>} />
            <Route path="notifications" element={<Suspense fallback={<LoadingFallback />}><NotificationSettings /></Suspense>} />
            <Route path="sessions" element={<Suspense fallback={<LoadingFallback />}><SessionsSettings /></Suspense>} />
            <Route path="encryption" element={<Suspense fallback={<LoadingFallback />}><EncryptionSettings /></Suspense>} />
            <Route path="encryption/setup" element={<Suspense fallback={<LoadingFallback />}><EncryptionSetupWizard /></Suspense>} />
            <Route path="encryption/recovery" element={<Suspense fallback={<LoadingFallback />}><EncryptionRecoveryWizard /></Suspense>} />
            <Route path="encryption/access-history" element={<Suspense fallback={<LoadingFallback />}><ShareAccessHistoryPage /></Suspense>} />
            <Route path="encryption/shares" element={<Suspense fallback={<LoadingFallback />}><EncryptionSettings /></Suspense>} />
            <Route path="encryption/redistribute" element={<Suspense fallback={<LoadingFallback />}><EncryptionSettings /></Suspense>} />
            <Route path="encryption/access-log" element={<Suspense fallback={<LoadingFallback />}><ShareAccessHistoryPage /></Suspense>} />
            <Route path="encryption/recovery-phrase" element={<Suspense fallback={<LoadingFallback />}><EncryptionSettings /></Suspense>} />
            <Route path="encryption/delete" element={<Suspense fallback={<LoadingFallback />}><EncryptionSettings /></Suspense>} />
            <Route path="connected" element={<Suspense fallback={<LoadingFallback />}><ProfileSettings /></Suspense>} />
            <Route path="data" element={<Suspense fallback={<LoadingFallback />}><DataExportSettings /></Suspense>} />
            <Route path="delete-account" element={<Suspense fallback={<LoadingFallback />}><AccountDeletionSettings /></Suspense>} />
            <Route path="billing" element={<Navigate to="/subscription" replace />} />
            {/* Context Profile routes (BRD-020) */}
            <Route path="context-profiles" element={<Suspense fallback={<LoadingFallback />}><ProfileListPage /></Suspense>} />
            <Route path="context-profiles/:id" element={<Suspense fallback={<LoadingFallback />}><ProfileEditorPage /></Suspense>} />
          </Route>

          {/* Redaction Settings routes (BRD-014) */}
          <Route path="/settings/redaction" element={<Navigate to="/settings/redaction/templates" replace />} />
          <Route path="/settings/redaction/templates" element={<Suspense fallback={<LoadingFallback />}><RedactionTemplateList /></Suspense>} />
          <Route path="/settings/redaction/templates/new" element={<Suspense fallback={<LoadingFallback />}><RedactionTemplateEditor /></Suspense>} />
          <Route path="/settings/redaction/templates/:id/edit" element={<Suspense fallback={<LoadingFallback />}><RedactionTemplateEditor /></Suspense>} />
          <Route path="/settings/redaction/templates/:id/analytics" element={<Suspense fallback={<LoadingFallback />}><RedactionTemplateAnalytics /></Suspense>} />
          {/* GAP-064/065/066: Template Coverage Overview */}
          <Route path="/settings/redaction/coverage" element={<Suspense fallback={<LoadingFallback />}><RedactionTemplateCoverage /></Suspense>} />

          {/* Redaction Interface route (BRD-015: Client-Side PII Detection & Redaction)
              GDPR Compliance: Redaction ONLY works with local files passed via navigation state.
              Documents are only uploaded to server AFTER redaction is complete. */}
          <Route path="/redaction" element={<Suspense fallback={<LoadingFallback />}><RedactionInterface /></Suspense>} />

          {/* Help Center (BRD-008 SCR-811) */}
          <Route path="/help" element={<Suspense fallback={<LoadingFallback />}><HelpCenterPage /></Suspense>} />

          {/* Security Info page (JM-001 Trust Foundation) */}
          <Route path="/security" element={<Suspense fallback={<LoadingFallback />}><SecurityInfoPage /></Suspense>} />

          {/* Nalatenschap (Estate) Dossier route */}
          <Route path="/nalatenschap" element={<Suspense fallback={<LoadingFallback />}><NalatenschapDossierPage /></Suspense>} />

          {/* Subscription routes (BRD-009) */}
          <Route path="/subscription" element={<Suspense fallback={<LoadingFallback />}><CurrentPlan /></Suspense>} />
          <Route path="/subscription/change" element={<Suspense fallback={<LoadingFallback />}><PlanChange /></Suspense>} />
          <Route path="/subscription/payment" element={<Suspense fallback={<LoadingFallback />}><PaymentMethods /></Suspense>} />
          <Route path="/subscription/invoices" element={<Suspense fallback={<LoadingFallback />}><BillingHistory /></Suspense>} />
          <Route path="/subscription/cancel" element={<Suspense fallback={<LoadingFallback />}><CancelSubscription /></Suspense>} />

          {/* Prompt Builder routes (BRD-016) */}
          <Route path="/prompt-builder" element={<Suspense fallback={<LoadingFallback />}><PromptBuilderPage /></Suspense>} />
          <Route path="/prompt-builder/templates" element={<Suspense fallback={<LoadingFallback />}><TemplateLibraryPage /></Suspense>} />
          <Route path="/prompt-builder/preview" element={<Suspense fallback={<LoadingFallback />}><PromptPreviewPage /></Suspense>} />
          <Route path="/prompt-builder/questionnaire/:domain" element={<Suspense fallback={<LoadingFallback />}><QuestionnairePage /></Suspense>} />
          <Route path="/prompt-builder/profile" element={<Suspense fallback={<LoadingFallback />}><MetadataProfilePage /></Suspense>} />
          <Route path="/prompt-builder/client-analysis" element={<Suspense fallback={<LoadingFallback />}><ClientAnalysisPromptPage /></Suspense>} />
          {/* SCR-321: Expert Content Contribution (Professional) */}
          <Route path="/prompt-builder/expert-content" element={<Suspense fallback={<LoadingFallback />}><ExpertContentPage /></Suspense>} />

          {/* Questionnaire routes (BRD-018) */}
          {/* SCR-QC-004: Consumer Wizard */}
          <Route path="/questionnaire/:questionnaireId" element={<Suspense fallback={<LoadingFallback />}><QuestionnaireWizard /></Suspense>} />
          {/* SCR-QC-001: Questionnaire List (Admin) */}
          <Route path="/admin/questionnaires" element={<Suspense fallback={<LoadingFallback />}><QuestionnaireListPage /></Suspense>} />
          {/* SCR-QC-002: Visual Flow Builder (Admin) */}
          <Route path="/admin/questionnaires/:questionnaireId/edit" element={<Suspense fallback={<LoadingFallback />}><VisualFlowBuilderPage /></Suspense>} />
          {/* SCR-QC-005: Analytics Dashboard (Admin) */}
          <Route path="/admin/questionnaires/:questionnaireId/analytics" element={<Suspense fallback={<LoadingFallback />}><AnalyticsDashboardPage /></Suspense>} />
          {/* SCR-QC-008: Preview Mode (Admin) */}
          <Route path="/admin/questionnaires/:questionnaireId/preview" element={<Suspense fallback={<LoadingFallback />}><PreviewModePage /></Suspense>} />
          {/* SCR-QC-006: Questionnaire Template Library (Admin) */}
          <Route path="/admin/questionnaires/templates" element={<Suspense fallback={<LoadingFallback />}><QuestionnaireTemplateLibraryPage /></Suspense>} />
          {/* SCR-QC-007: Version History (Admin) */}
          <Route path="/admin/questionnaires/:questionnaireId/versions" element={<Suspense fallback={<LoadingFallback />}><VersionHistoryPage /></Suspense>} />

          {/* Marketplace routes (BRD-011) */}
          <Route path="/marketplace" element={<Suspense fallback={<LoadingFallback />}><MarketplaceBrowse /></Suspense>} />
          <Route path="/marketplace/browse" element={<Suspense fallback={<LoadingFallback />}><MarketplaceBrowse /></Suspense>} />
          <Route path="/marketplace/requests" element={<Suspense fallback={<LoadingFallback />}><MyServiceRequests /></Suspense>} />
          <Route path="/marketplace/benchmark/:documentId" element={<Suspense fallback={<LoadingFallback />}><BenchmarkViewWrapper /></Suspense>} />
          <Route path="/marketplace/connections/:connectionId" element={<Suspense fallback={<LoadingFallback />}><ConnectionChat /></Suspense>} />
          <Route path="/marketplace/providers/:providerId" element={<Suspense fallback={<LoadingFallback />}><ProviderProfile /></Suspense>} />
          {/* Provider-specific routes (for professionals) */}
          <Route path="/marketplace/provider/dashboard" element={<Suspense fallback={<LoadingFallback />}><ProviderDashboard /></Suspense>} />
          <Route path="/marketplace/provider/requests" element={<Suspense fallback={<LoadingFallback />}><ProviderRequestsFeed /></Suspense>} />
          <Route path="/marketplace/provider/requests/:requestId/respond" element={<Suspense fallback={<LoadingFallback />}><ProviderRequestRespond /></Suspense>} />
          <Route path="/marketplace/provider/connections" element={<Suspense fallback={<LoadingFallback />}><ProviderConnections /></Suspense>} />

        {/* Advisor routes (BRD-019: consolidated professional/intermediary) - lazy loaded */}
          <Route
            path="/advisor/*"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AdvisorDashboard />
              </Suspense>
            }
          />

          {/* Legacy route redirects (BRD-019: professional/intermediary → advisor) */}
          <Route path="/professional/*" element={<Navigate to="/advisor/dashboard" replace />} />
          <Route path="/intermediary/*" element={<Navigate to="/advisor/dashboard" replace />} />

        {/* Tenant admin routes - lazy loaded */}
          <Route
            path="/admin/*"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <TenantAdmin />
              </Suspense>
            }
          />
        </Route>

        {/* Development persona switcher (BRD-DEV-001) */}
        {import.meta.env.DEV && (
          <Route
            path="/dev"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <DevPersonaSwitcher />
              </Suspense>
            }
          />
        )}

        {/* Development anonymization test page (BRD-DEV) */}
        {import.meta.env.DEV && (
          <Route
            path="/dev/anonymize"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <AnonymizeTestPage />
              </Suspense>
            }
          />
        )}

        {/* Development Ollama Chat page (BRD-DEVMODE-001) */}
        {import.meta.env.DEV && (
          <Route
            path="/dev/ollama"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <DevModePage />
              </Suspense>
            }
          />
        )}

        {/* Development API Mock Configuration page (BRD-DEV-002) */}
        {import.meta.env.DEV && (
          <Route
            path="/dev/api-mocks"
            element={
              <Suspense fallback={<LoadingFallback />}>
                <MockConfigPage />
              </Suspense>
            }
          />
        )}

        {/* Development Redaction Templates (BRD-014) */}
        {import.meta.env.DEV && (
          <>
            <Route
              path="/dev/redaction"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RedactionTemplateList />
                </Suspense>
              }
            />
            <Route
              path="/dev/redaction/new"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RedactionTemplateEditor />
                </Suspense>
              }
            />
            <Route
              path="/dev/redaction/:id/edit"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RedactionTemplateEditor />
                </Suspense>
              }
            />
            <Route
              path="/dev/redaction/:id/analytics"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RedactionTemplateAnalytics />
                </Suspense>
              }
            />
          </>
        )}

        {/* Catch-all - 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </SearchProvider>
  );
}
